// Gestionnaire d'entrée : une seule source principale à la fois (micro, MIDI, synthèse
// ou clavier d'essai) ; le clavier affiché à l'écran reste toujours utilisable au clic.
// Les écrans s'abonnent ici, sans connaître la source.

import type { InputEvent, InputKind } from '../engine/types';
import { KeyboardInputSource } from './keyboard';
import { MicInputSource } from './mic';
import { MidiInputSource } from './midi';
import type { EventListener, InputSource, InputStatus } from './types';

export type InputSelection =
  | { kind: 'midi'; deviceId: string | null }
  | { kind: 'mic'; deviceId: string | null; latencyMs: number }
  | { kind: 'synth'; latencyMs: number }
  | { kind: 'keyboard' };

const IDLE: InputStatus = { kind: 'keyboard', state: 'idle', message: 'Aucune source active.', devices: [] };

export class InputManager {
  private source: InputSource | null = null;
  private selection: InputSelection | null = null;
  private readonly listeners = new Set<EventListener>();
  private readonly statusListeners = new Set<() => void>();
  private unsubscribeSource: (() => void)[] = [];
  private status: InputStatus = IDLE;
  readonly keyboard = new KeyboardInputSource();

  constructor() {
    // Les clics sur le clavier affiché passent par la source clavier, quelle que soit la source principale.
    // Avec la source de synthèse, une touche joue un son type piano dans la chaîne d'analyse au lieu
    // d'émettre directement une note : c'est la détection qui produit l'événement.
    this.keyboard.subscribe((e) => {
      const synth = this.source instanceof MicInputSource && this.source.synthetic ? this.source : null;
      if (synth) {
        if (e.type === 'noteOn') synth.playSyntheticNote(e.pitch);
        else if (e.type === 'connection') this.dispatch(e);
        return;
      }
      this.dispatch(e);
    });
  }

  get currentSelection(): InputSelection | null {
    return this.selection;
  }

  get currentKind(): InputKind | null {
    return this.source?.kind ?? null;
  }

  get mic(): MicInputSource | null {
    return this.source instanceof MicInputSource ? this.source : null;
  }

  get midi(): MidiInputSource | null {
    return this.source instanceof MidiInputSource ? this.source : null;
  }

  getStatus(): InputStatus {
    return this.status;
  }

  subscribe(listener: EventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Abonnement aux changements d'état (compatible useSyncExternalStore). */
  onStatusChange(callback: () => void): () => void {
    this.statusListeners.add(callback);
    return () => this.statusListeners.delete(callback);
  }

  async select(selection: InputSelection): Promise<void> {
    this.stop();
    this.selection = selection;
    let source: InputSource;
    switch (selection.kind) {
      case 'midi':
        source = new MidiInputSource({ deviceId: selection.deviceId });
        break;
      case 'mic':
        source = new MicInputSource({ deviceId: selection.deviceId, latencyMs: selection.latencyMs });
        break;
      case 'synth':
        source = new MicInputSource({ latencyMs: selection.latencyMs, synthetic: true });
        break;
      case 'keyboard':
        source = this.keyboard;
        break;
    }
    this.source = source;
    if (source !== this.keyboard) this.unsubscribeSource.push(source.subscribe((e) => this.dispatch(e)));
    this.unsubscribeSource.push(source.onStatus((s) => this.setStatus(s)));
    this.setStatus(source.getStatus());
    await source.start();
    // Les touches de l'ordinateur ne sont écoutées qu'avec le clavier d'essai ou la source de synthèse.
    if (selection.kind === 'synth') await this.keyboard.start();
  }

  stop(): void {
    for (const u of this.unsubscribeSource) u();
    this.unsubscribeSource = [];
    if (this.source) this.source.stop();
    if (this.source !== this.keyboard) this.keyboard.stop();
    this.source = null;
    this.selection = null;
    this.setStatus(IDLE);
  }

  /** Met à jour la compensation de latence sans redémarrer le micro. */
  setLatency(ms: number): void {
    if (this.source instanceof MicInputSource) this.source.setLatency(ms);
    if (this.selection && (this.selection.kind === 'mic' || this.selection.kind === 'synth')) this.selection = { ...this.selection, latencyMs: ms };
  }

  private dispatch(e: InputEvent): void {
    for (const l of this.listeners) l(e);
  }

  private setStatus(s: InputStatus): void {
    this.status = s;
    for (const cb of this.statusListeners) cb();
  }
}

export const inputManager = new InputManager();
